/** @jsx jsx */
import { css, jsx, keyframes } from '@emotion/react';

import { useContext, useEffect, useState } from 'react';

import { Avatar, Grid, InlineNotification, Typography2 } from '@coursera/cds-core';
import type { Theme } from '@coursera/cds-core';

import { InviteToApplyContext } from 'bundles/account-profile/components/recruiting-profile-viewer/InviteToApplyProvider';
import { ProfileContext } from 'bundles/account-profile/components/recruiting-profile-viewer/ProfileContextProvider';
import StickyBanner, {
  NAV_BAR_HEIGHT,
} from 'bundles/account-profile/components/recruiting-profile-viewer/components/StickyBanner';
import InviteToApplyButton from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/InviteToApplyButton';
import { PROFILE_SECTION_QUERY_SELECTOR } from 'bundles/account-profile/components/recruiting-profile-viewer/components/profile-and-invitation/PhotoAndInvitationSection';
import { EVENTS } from 'bundles/account-profile/components/recruiting-profile-viewer/eventing/events';
import ErrorBoundaryWithTagging from 'bundles/common/components/ErrorBoundaryWithTagging';

import _t from 'i18n!nls/account-profile';

const PROFILE_PHOTO_DIMENSION = 36;

const slideDown = keyframes`
from {
  transform: translateY(-${NAV_BAR_HEIGHT}px);
  opacity: 0;
}
to {
  transform: translateY(0);
  opacity: 1;
}
`;

const slideUp = keyframes`
from {
  transform: translateY(0);
  opacity: 1;
}
to {
  transform: translateY(-${NAV_BAR_HEIGHT}px);
  opacity: 0;
}
`;

const styles = {
  imgWrapper: (theme: Theme) => css`
    margin-right: ${theme.spacing(16)};
  `,
  wrapper: (theme: Theme) => css`
    padding: ${theme.spacing(16)} ${theme.spacing(48)};
    background-color: ${theme.palette.blue[100]};
    box-shadow: 0 4px 12px rgb(0 0 0 / 10%);

    ${theme.breakpoints.down('xs')} {
      display: none;
    }
  `,
  animations: (shouldShowBanner: boolean) => css`
    -webkit-animation: ${shouldShowBanner ? slideDown : slideUp} 1s ease-out;
    animation: ${shouldShowBanner ? slideDown : slideUp} 1s ease-out;
    opacity: ${shouldShowBanner ? 1 : 0};
    display: ${shouldShowBanner ? 'visible' : 'hidden'};
  `,
  candidateInfo: css`
    display: flex;
  `,
};

const InviteToApplyBanner = () => {
  const [shouldDisplayBanner, setShouldDisplayBanner] = useState<boolean>(false);
  // We don't want the animation on first mount
  const [hasScrolledDown, setHasScrolledDown] = useState<boolean>(false);
  const { profileState = {}, isProfileLoading, isRecruiter } = useContext(ProfileContext);
  const { setShowInviteToApplyModal } = useContext(InviteToApplyContext);
  const { profilePhoto, fullName = '' } = profileState;

  useEffect(() => {
    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((target) => {
          const { isIntersecting } = target;
          setShouldDisplayBanner(!isIntersecting);

          if (!hasScrolledDown && !isIntersecting) setHasScrolledDown(true);
        });
      },
      { rootMargin: `-${NAV_BAR_HEIGHT}px 0px` }
    );

    const target = document.querySelector(`#${PROFILE_SECTION_QUERY_SELECTOR}`);
    if (target) {
      intersectionObserver.observe(target);
      return () => intersectionObserver.unobserve(target);
    }

    return () => intersectionObserver.disconnect();
  }, [hasScrolledDown]);

  if (isProfileLoading || !isRecruiter || !hasScrolledDown) return null;

  return (
    <StickyBanner>
      <Grid
        container
        direction="row"
        alignItems="center"
        wrap="nowrap"
        justifyContent="space-between"
        css={hasScrolledDown ? [styles.wrapper, styles.animations(shouldDisplayBanner)] : styles.wrapper}
        data-testid="invite-to-apply-banner"
      >
        <div css={styles.candidateInfo}>
          <div>
            <div css={styles.imgWrapper} className="fs-exclude">
              <Avatar
                showBorder
                variant="user"
                initial={fullName?.[0]}
                imgProps={{
                  src: profilePhoto?.default,
                  alt: _t('Profile photo'),
                }}
                size={PROFILE_PHOTO_DIMENSION}
              />
            </div>
          </div>
          <Grid container item direction="column">
            <Typography2 component="p" variant="bodySecondary">
              {_t('Candidate')}
            </Typography2>
            <Typography2 component="h3" variant="subtitleMedium" className="pii-hide fs-exclude">
              {fullName}
            </Typography2>
          </Grid>
        </div>
        <InviteToApplyButton
          onClick={() => setShowInviteToApplyModal(true)}
          variant="primary"
          size="small"
          trackingName={EVENTS.inviteToApplyBanner}
        />
      </Grid>
    </StickyBanner>
  );
};

const ErrorBoundInviteToApplyBanner = () => {
  return (
    <ErrorBoundaryWithTagging
      componentName="InviteToApplyBanner"
      errorComponent={
        <InlineNotification severity="error" title={_t('There has been an error')} role="alert">
          {_t('There has been an error in the invite to apply banner. Our development team will be alerted.')}
        </InlineNotification>
      }
    >
      <InviteToApplyBanner />
    </ErrorBoundaryWithTagging>
  );
};

export default ErrorBoundInviteToApplyBanner;
